import { hasUsableQueueEntries } from '../components/bulk/bulkFlow';

export const SAME_ORIGIN_API_IDENTITY = 'same-origin';
export const LEGACY_UNBOUND_API_IDENTITY = 'legacy-unbound';

/**
 * Convert runtime API configuration into the stable identity stored with a
 * retry handle. The empty URL is Vite's same-origin proxy; sessionStorage is
 * already scoped to the frontend origin, so an explicit sentinel is stable.
 */
export function canonicalizeRetryApiIdentity(apiUrl: string): string {
  const value = String(apiUrl || '').trim();
  if (!value || value === SAME_ORIGIN_API_IDENTITY) return SAME_ORIGIN_API_IDENTITY;
  if (value === LEGACY_UNBOUND_API_IDENTITY) return LEGACY_UNBOUND_API_IDENTITY;

  try {
    const parsed = new URL(value);
    const path = parsed.pathname.replace(/\/+$/, '');
    // URL canonicalizes protocol/host case and strips a default port.
    return `${parsed.origin}${path && path !== '/' ? path : ''}`;
  } catch {
    return value.replace(/\/+$/, '');
  }
}

/**
 * Prepared EasyPost IDs are scoped to the Worker/account that created them.
 * Tokens may rotate, but retrying through a different Worker must fail closed.
 */
export function retryApiIdentityMatches(expectedApiUrl: string, currentApiUrl: string): boolean {
  const expected = canonicalizeRetryApiIdentity(expectedApiUrl);
  const current = canonicalizeRetryApiIdentity(currentApiUrl);
  return expected !== LEGACY_UNBOUND_API_IDENTITY
    && current !== LEGACY_UNBOUND_API_IDENTITY
    && expected === current;
}

export function hasPreparedBulkRecoveryState(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  if (record.version !== 2 && record.version !== 3) return false;
  if (!Array.isArray(record.items)) return false;
  return record.items.some((item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return false;
    return typeof (item as Record<string, unknown>).previewShipmentId === 'string'
      && Boolean(String((item as Record<string, unknown>).previewShipmentId).trim());
  });
}

/** Only a readable, supported queue without preview handles can be replaced implicitly. */
export function requiresBulkQueueDiscard(raw: string | null): boolean {
  if (raw === null) return false;
  try {
    const value: unknown = JSON.parse(raw);
    if (Array.isArray(value)) return !hasUsableQueueEntries(value.map(address => ({ address })));
    if (!value || typeof value !== 'object') return true;
    const record = value as Record<string, unknown>;
    if (record.version !== 2 && record.version !== 3) return true;
    return !hasUsableQueueEntries(record.items) || hasPreparedBulkRecoveryState(record);
  } catch { return true; }
}

export function resolveStoredBulkApiIdentity(value: unknown, currentApiUrl: string): string {
  const current = canonicalizeRetryApiIdentity(currentApiUrl);
  if (!hasPreparedBulkRecoveryState(value)) return current;

  const record = value as Record<string, unknown>;
  if (record.version === 2) return LEGACY_UNBOUND_API_IDENTITY;
  return typeof record.apiUrl === 'string' && record.apiUrl.trim()
    ? canonicalizeRetryApiIdentity(record.apiUrl)
    : LEGACY_UNBOUND_API_IDENTITY;
}
